"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-8">
        <div className="h-4 w-16 bg-muted animate-pulse" />
        <div className="h-4 w-16 bg-muted animate-pulse" />
        <div className="h-4 w-16 bg-muted animate-pulse" />
        <div className="h-4 w-16 bg-muted animate-pulse" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem data-index={i} key={item.id}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative inline-flex items-center gap-2 bg-transparent hover:bg-transparent focus:bg-transparent px-0 py-2 text-foreground">
                <span className="font-mono text-[10px] tracking-widest text-muted-foreground" aria-hidden="true">
                  [{String(i + 1).padStart(2, "0")}]
                </span>
                <span className="text-[13px] font-bold uppercase tracking-[0.15em] font-default">
                  {item.label}
                </span>
                <span className="absolute left-0 -bottom-1 h-px w-0 bg-primary transition-all duration-[400ms] ease-out group-hover:w-full" aria-hidden="true" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 px-6">
        <div className="h-12 w-3/4 bg-muted animate-pulse" />
        <div className="h-12 w-3/4 bg-muted animate-pulse" />
        <div className="h-12 w-3/4 bg-muted animate-pulse" />
      </div>;
  }
  return <nav className="flex flex-col gap-2 px-6">
      {menuItems.map((item, i) => <Link data-index={i} key={item.id} to={item.href || "#"} onClick={onItemClick} className="group flex items-baseline gap-3 py-3 border-b border-border">
          <span className="font-mono text-xs tracking-widest text-white/70" aria-hidden="true">
            [{String(i + 1).padStart(2, "0")}]
          </span>
          <span className="text-3xl font-bold uppercase tracking-tight font-default text-white group-hover:text-primary transition-colors duration-300">
            {item.label}
          </span>
        </Link>)}
    </nav>;
}
export default function MainNavigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    if (typeof document !== "undefined") {
      document.addEventListener("click", handleAnchorClick);
    }
    return () => {
      if (typeof document !== "undefined") {
        document.removeEventListener("click", handleAnchorClick);
      }
    };
  }, []);
  return <section data-section-id="3541" id="main-navigation" className="sticky top-0 z-50 w-full text-foreground transition-all duration-500 ease-out backdrop-blur-xl border-b border-border shadow-lg dark bg-accent !h-[90px]">
      <div className="mx-auto w-full max-w-[1400px] px-6 !h-[95px] lg:px-10">
        <div className="grid grid-cols-2 lg:grid-cols-12 items-center h-20 gap-4">
          <div className="lg:col-span-3 flex items-center">
            <Link to="/HomePage" className="inline-flex items-center text-primary" aria-label="Infinix Solutions - Home">
              <WvcLogo className="h-12 w-auto [&_svg]:h-12 [&_svg]:w-auto" />
            </Link>
          </div>

          <div className="hidden lg:flex lg:col-span-6 items-center justify-center">
            <MenuProvider menu_id="16" className="header-menu">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          <div className="hidden lg:flex lg:col-span-3 items-center justify-end">
            <Button asChild variant="outline" className="h-11 px-6 bg-transparent border border-border text-foreground text-[12px] font-bold uppercase tracking-[0.18em] font-default hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors duration-300 rounded-lg">
              <Link to="/GetInTouchPage">GET IN TOUCH</Link>
            </Button>
          </div>

          <div className="flex lg:hidden items-center justify-end">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-foreground hover:bg-transparent hover:text-primary" aria-label="Open menu">
                  <Menu className="size-6" strokeWidth={1.5} />
                </Button>
              </SheetTrigger>
              <SheetContent side="top" className="data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 gap-4 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-screen w-full max-w-full sm:max-w-full text-foreground border-b border-border p-0 flex flex-col bg-primary">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <div className="flex items-center justify-center h-20 border-b border-border bg-primary">
                  <Link to="/HomePage" onClick={() => setMobileOpen(false)} className="inline-flex items-center text-primary" aria-label="Infinix Solutions - Home">
                    <WvcLogo className="h-12 w-auto" />
                  </Link>
                </div>

                <div className="flex-1 flex flex-col justify-center py-10 overflow-y-auto bg-primary">
                  <MenuProvider menu_id="16" className="header-menu-mobile">
                    <MobileMenuConsumer onItemClick={() => setMobileOpen(false)} />
                  </MenuProvider>
                </div>

                <div className="p-6 border-t border-border">
                  <Button asChild className="w-full h-14 bg-primary text-primary-foreground text-[13px] font-bold uppercase tracking-[0.2em] font-default hover:bg-primary/90 rounded-lg shadow-xl">
                    <Link to="/GetInTouchPage" onClick={() => setMobileOpen(false)}>
                      Book Consultation
                    </Link>
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}